<script>
import api from "@/helpers/api-rest/api.js";
import store from "@/state/store";
import qs from "qs";
import { required, requiredIf } from "vuelidate/lib/validators";
import Swal from "sweetalert2";

// Required fields configuration for Anexo VI download
const ANEXO_VI_REQUIRED_FIELDS = {
  reason: 'Justificación',
  name: 'Nombre',
  cif: 'CIF',
};

export default {
  props: {
    id: { type: String, default: "ModalAccessionCompany" },
    essayId: { type: [Number, String] },
    centerId: { type: Number },
    accesion_company: {
      type: Object,
      default: () => {
        return {
          id: null,
          document_name: null,
        };
      },
    },
  },
  data() {
    return {
      isModalOpen: false,
      dataUser: [],
      selectOptionsTrackingStatus: [],
      selectOptionsActions: [],
      collapseOpen: [],
      loading: false,
      downloadingAnexoVI: false,
      uploadingFile: false,
      validateFile: false,
    };
  },
  computed: {
    modalTitle() {
      return this.accesion_company?.id ? "Editar adhesión" : "Añadir adhesión";
    },
    submitButtonText() {
      return this.accesion_company?.id ? "Guardar" : "Añadir";
    },
    canDownloadAnexoVI() {
      return this.validateRequiredFieldsForAnexoVI().isValid;
    },
  },
  validations() {
    return {
      accesion_company: {
        reason: { required },
        name: { required },
        cif: { required },
        address: { required },
        responsable: { required },
        email: { required },
        document_path: {
          required: requiredIf(function () {
            return this.validateFile;
          }),
        },
      },
    };
  },
  methods: {
    validateRequiredFieldsForAnexoVI() {
      const fieldValues = Object.keys(ANEXO_VI_REQUIRED_FIELDS).reduce((acc, key) => {
        acc[key] = this.accesion_company?.[key];
        return acc;
      }, {});

      const missingFields = Object.entries(fieldValues)
        .filter(([, value]) => !value || (typeof value === 'string' && value.trim() === ''))
        .map(([key]) => key);

      const missingFieldNames = missingFields.map(field => ANEXO_VI_REQUIRED_FIELDS[field]).join(', ');

      return {
        isValid: missingFields.length === 0,
        missingFields,
        missingFieldNames,
      };
    },
    showElement(roleId = [], show = true, showEdit = true) {
      const role = store.getters["auth/role"];
      if (roleId.includes(role.id) && show && showEdit) {
        return true;
      }
      return false;
    },
    onFileChange(event) {
      this.file = event.target.files[0];
      this.accesion_company.document_name = this.file?.name;
      this.uploadingFile = true;
      this.uploadFile(this.file, "accession")
        .then((response) => {
          const file = response?.data?.data;
          this.accesion_company.document_path = file.path;
        })
        .catch((error) => {
          console.error("Error al cargar el archivo:", error);
          // Reset file name and path on error
          this.accesion_company.document_name = null;
          this.accesion_company.document_path = null;
        })
        .finally(() => {
          this.uploadingFile = false;
        });
    },
    uploadFile(file, folder) {
      const formData = new FormData();
      formData.set("file", file);
      formData.append("path", folder);
      const config = { headers: { "content-type": "multipart/form-data" } };

      const apiEndpoint = `/api/v1/upload-file`;
      return api
        .post({ url: apiEndpoint, data: formData, config })
        .catch((error) => {
          this.loading = false;
          console.error("Error upload file:", error);

          // Extract error message from response
          const errorMessage =
            error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message ||
            "Error al subir el archivo. Por favor, inténtelo de nuevo.";

          Swal.fire({
            icon: "error",
            title: "Error al subir el archivo",
            html: errorMessage,
            confirmButtonColor: "#34c38f",
            confirmButtonText: "Aceptar",
          });
        });
    },
    async downloadFileAnexoVI() {
      if (this.downloadingAnexoVI) return;
      
      // Validate required fields before downloading
      const validation = this.validateRequiredFieldsForAnexoVI();
      
      if (!validation.isValid) {
        Swal.fire({
          icon: "warning",
          title: "Campos requeridos",
          html: `Debes completar los siguientes campos antes de descargar el Anexo VI:<br><strong>${validation.missingFieldNames}</strong>`,
          confirmButtonColor: "#34c38f",
          confirmButtonText: "Aceptar",
        });
        return;
      }
      
      this.downloadingAnexoVI = true;
      try {
        const apiEndpoint = `/api/v1/anexo6`;
        const data = {
          accesion_company: this.accesion_company,
          essay_id: this.essayId,
          center_id: this.centerId,
        };

        const config = {
          responseType: "blob",
          headers: {
            "Content-Type": "application/json",
          },
        };

        const response = await api.post({
          url: apiEndpoint,
          data: data,
          baseURL: process.env.VUE_APP_API_HOST,
          config: config,
        });

        const blob = new Blob([response.data], { type: "application/pdf" });
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = "anexoVI.pdf";
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
      } catch (error) {
        console.error("Error al descargar el Anexo VI:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Hubo un error al descargar el Anexo VI. Por favor, inténtalo de nuevo.",
        });
      } finally {
        this.downloadingAnexoVI = false;
      }
    },
    downloadFiles(fileName, folderID) {
      const params = {
        fileName: fileName,
        folderID: folderID,
      };

      const queryString = qs.stringify(params);
      const baseUrl = process.env.VUE_APP_API_HOST;
      const url = `${baseUrl}/download-drive?${queryString}`;
      window.open(url, "_blank");
    },
    createAccessionCompany() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.warningMsgValidation();
        return;
      }
      this.loading = true;
      const apiEndpoint = `/api/v1/accession-company-partners`;
      this.accesion_company.essay_id = this.essayId;
      api
        .post({ url: apiEndpoint, data: this.accesion_company })
        .then((response) => {
          this.loading = false;
          const newAccession = response?.data?.data;
          this.$emit("success", newAccession);
          this.$bvModal.hide(this.id);
        })
        .catch((error) => {
          this.loading = false;
          console.error("Error al crear adhesion de empresa:", error);
        });
    },
    updateAccessionCompany() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.warningMsgValidation();
        return;
      }
      this.loading = true;
      const apiEndpoint = `/api/v1/accession-company-partners`;
      api
        .put({
          url: apiEndpoint,
          data: this.accesion_company,
          id: this.accesion_company.id,
        })
        .then((response) => {
          this.loading = false;
          const updatedAccession = response?.data?.data;
          this.$emit("success", updatedAccession);
          this.$bvModal.hide(this.id);
        })
        .catch((error) => {
          this.loading = false;
          console.error("Error al editar adhesion de empresa:", error);
        });
    },
    changeStatusAccession(status) {
      this.accesion_company.status = status;
      this.submitForm();
    },
    submitForm() {
      // Prevent form submission if file is still uploading
      if (this.uploadingFile) {
        Swal.fire({
          icon: "warning",
          title: "Espera",
          text: "Por favor, espera a que termine la subida del documento antes de guardar.",
          confirmButtonColor: "#34c38f",
          confirmButtonText: "Aceptar",
        });
        return;
      }
      if (this.accesion_company?.id) {
        return this.updateAccessionCompany();
      }
      return this.createAccessionCompany();
    },
    cancelForm() {
      this.$bvModal.hide(this.id);
    },
    saveAccession(status_user, validateFile = false) {
      // Prevent form submission if file is still uploading
      if (this.uploadingFile) {
        Swal.fire({
          icon: "warning",
          title: "Espera",
          text: "Por favor, espera a que termine la subida del documento antes de guardar.",
          confirmButtonColor: "#34c38f",
          confirmButtonText: "Aceptar",
        });
        return;
      }
      this.validateFile = validateFile;
      this.$v.$reset();
      this.accesion_company.status_user = status_user;
      this.submitForm();
    },
    addAccession(status_user) {
      // Alias method that calls saveAccession with validateFile = true
      this.saveAccession(status_user, true);
    },
    loadData() {
      this.$v.$reset();
      this.uploadingFile = false;
      this.validateFile = false;
      console.log(this.essayId, typeof this.essayId);
      console.log(this.accesion_company);
    },
    warningMsgValidation() {
      Swal.fire({
        icon: "error",
        html: "Hay campos requeridos sin completar",
        timer: 2000,
        showConfirmButton: false,
      });
    },
  },
};
</script>
<template>
  <div>
    <b-modal
      :id="id"
      :title="modalTitle"
      @shown="loadData"
      scrollable
      size="xl"
      no-close-on-backdrop
      no-close-on-esc
    >
      <template #default="{}">
        <form @submit.prevent="submitForm" enctype="multipart/form-data">
          <div class="card">
            <div class="card-body">
              <div class="row">
                <div class="col-lg-12">
                  <h6>Justificación <span style="color: red">*</span></h6>
                  <p v-if="showElement([1])" class="text-muted">
                    {{ accesion_company?.reason }}
                  </p>
                  <div v-else class="form-group">
                    <textarea
                      v-model="accesion_company.reason"
                      class="form-control"
                      rows="3"
                      placeholder="Introduce justificación"
                      :class="{
                        'is-invalid': $v.accesion_company.reason.$error,
                      }"
                    ></textarea>
                    <div
                      v-if="$v.accesion_company.reason.$error"
                      class="invalid-feedback"
                    >
                      <div v-if="!$v.accesion_company.reason.required">
                        Este campo es requerido.
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-lg-6">
                  <h6>Nombre <span style="color: red">*</span></h6>
                  <p v-if="showElement([1])" class="text-muted">
                    {{ accesion_company?.name }}
                  </p>
                  <div v-else class="form-group">
                    <input
                      v-model="accesion_company.name"
                      type="text"
                      class="form-control"
                      id="name"
                      placeholder="Nombre"
                      :class="{ 'is-invalid': $v.accesion_company.name.$error }"
                    />
                    <div
                      v-if="$v.accesion_company.name.$error"
                      class="invalid-feedback"
                    >
                      <div v-if="!$v.accesion_company.name.required">
                        Este campo es requerido.
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-lg-6">
                  <h6>CIF <span style="color: red">*</span></h6>
                  <p v-if="showElement([1])" class="text-muted">
                    {{ accesion_company?.cif }}
                  </p>
                  <div v-else class="form-group">
                    <input
                      v-model="accesion_company.cif"
                      type="text"
                      class="form-control"
                      id="cif"
                      placeholder="CIF"
                      :class="{ 'is-invalid': $v.accesion_company.cif.$error }"
                    />
                    <div
                      v-if="$v.accesion_company.cif.$error"
                      class="invalid-feedback"
                    >
                      <div v-if="!$v.accesion_company.cif.required">
                        Este campo es requerido.
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-lg-12">
                  <h6>Dirección <span style="color: red">*</span></h6>
                  <p v-if="showElement([1])" class="text-muted">
                    {{ accesion_company?.address }}
                  </p>
                  <div v-else class="form-group">
                    <textarea
                      v-model="accesion_company.address"
                      class="form-control"
                      rows="3"
                      placeholder="Dirección"
                      :class="{
                        'is-invalid': $v.accesion_company.address.$error,
                      }"
                    ></textarea>
                    <div
                      v-if="$v.accesion_company.address.$error"
                      class="invalid-feedback"
                    >
                      <div v-if="!$v.accesion_company.address.required">
                        Este campo es requerido.
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-lg-6">
                  <h6>Responsable <span style="color: red">*</span></h6>
                  <p v-if="showElement([1])" class="text-muted">
                    {{ accesion_company?.responsable }}
                  </p>
                  <div v-else class="form-group">
                    <input
                      v-model="accesion_company.responsable"
                      type="text"
                      class="form-control"
                      id="responsable"
                      placeholder="Responsable"
                      :class="{
                        'is-invalid': $v.accesion_company.responsable.$error,
                      }"
                    />
                    <div
                      v-if="$v.accesion_company.responsable.$error"
                      class="invalid-feedback"
                    >
                      <div v-if="!$v.accesion_company.responsable.required">
                        Este campo es requerido.
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-lg-6">
                  <h6>Email <span style="color: red">*</span></h6>
                  <p v-if="showElement([1])" class="text-muted">
                    {{ accesion_company?.email }}
                  </p>
                  <div v-else class="form-group">
                    <input
                      v-model="accesion_company.email"
                      type="text"
                      class="form-control"
                      id="email"
                      placeholder="Email"
                      :class="{
                        'is-invalid': $v.accesion_company.email.$error,
                      }"
                    />
                    <div
                      v-if="$v.accesion_company.email.$error"
                      class="invalid-feedback"
                    >
                      <div v-if="!$v.accesion_company.email.required">
                        Este campo es requerido.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="showElement([3])" class="row mt-4">
                <div class="col-lg-12">
                  <b-alert show variant="warning">
                    <strong>Observaciones</strong><br />
                    <ul>
                      <li>
                        Antes de adjuntar el <strong>Anexo VI</strong>, primero
                        debes generarlo
                        <a
                          href="javascript:void(0);"
                          @click.prevent="downloadFileAnexoVI()"
                          :class="{ 
                            'text-muted': downloadingAnexoVI,
                            'text-danger': !canDownloadAnexoVI && !downloadingAnexoVI
                          }"
                          :style="{ 
                            cursor: downloadingAnexoVI ? 'not-allowed' : 'pointer',
                            pointerEvents: downloadingAnexoVI ? 'none' : 'auto'
                          }"
                          :title="!canDownloadAnexoVI ? 'Debes completar los campos requeridos (Justificación, Nombre, CIF, Dirección) antes de descargar' : ''"
                        >
                          <span
                            v-if="downloadingAnexoVI"
                            class="spinner-border spinner-border-sm mr-1"
                            role="status"
                            aria-hidden="true"
                            style="width: 0.875rem; height: 0.875rem;"
                          ></span>
                          {{ downloadingAnexoVI ? 'descargando' : 'aquí' }}
                        </a>
                      </li>
                      <li>
                        Si has completado los dos campos anteriores, también
                        aparecerán en el <strong>Anexo VI</strong>
                      </li>
                    </ul>
                  </b-alert>
                </div>
                <div
                  v-if="
                    showElement(
                      [3],
                      accesion_company?.essay?.essay_status_id != 7
                    )
                  "
                  class="col-lg-6 mt-3"
                >
                  <h6>Adjuntar Anexo VI <span style="color: red">*</span></h6>
                  <div class="form-group">
                    <div class="custom-file">
                      <input
                        id="inputFile"
                        type="file"
                        name="file"
                        class="custom-file-input"
                        @change="onFileChange"
                        :disabled="uploadingFile || [4, 6, 7].includes(accesion_company?.essay?.essay_status_id)"
                        :class="{
                          'is-invalid':
                            $v.accesion_company.document_path.$error,
                        }"
                      />
                      <label class="custom-file-label" for="inputFile">{{
                        accesion_company.document_name ||
                        "Seleccione un archivo"
                      }}</label>
                      <div
                        v-if="$v.accesion_company.document_path.$error"
                        class="invalid-feedback"
                      >
                        <div v-if="!$v.accesion_company.document_path.required">
                          Este campo es requerido.
                        </div>
                      </div>
                      <div v-if="uploadingFile" class="mt-2 text-info">
                        <span
                          class="spinner-border spinner-border-sm mr-2"
                          role="status"
                          aria-hidden="true"
                          style="width: 0.875rem; height: 0.875rem;"
                        ></span>
                        Subiendo documento...
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="row mt-3">
                <div class="col-lg-12 mt-3">
                  <h6>Anexo VI</h6>
                  <b-button
                    variant="info"
                    @click.prevent="
                      downloadFiles(
                        accesion_company?.document_name,
                        accesion_company?.essay?.folder_documents_id
                      )
                    "
                    ><i class="fas fa-file-download mr-2"></i>
                    Descargar</b-button
                  >
                </div>
              </div>
            </div>
          </div>
        </form>
      </template>
      <template #modal-footer="{}">
        <div class="d-flex justify-content-between w-100">
          <b-button
            v-if="
              showElement(
                [3],
                ![4, 6, 7].includes(accesion_company?.essay?.essay_status_id)
              )
            "
            variant="primary"
            @click="saveAccession('editando')"
            :disabled="loading || uploadingFile"
            ><span
              class="spinner-border spinner-border-sm mr-2"
              role="status"
              aria-hidden="true"
              v-if="loading"
            ></span
            >Guardar adhesión</b-button
          >
          <div>
            <b-button
              v-if="
                showElement(
                  [3],
                  ![4, 6, 7].includes(accesion_company?.essay?.essay_status_id)
                )
              "
              class="mr-2"
              variant="success"
              @click="addAccession('finalizado')"
              :disabled="loading || uploadingFile"
              ><span
                class="spinner-border spinner-border-sm mr-2"
                role="status"
                aria-hidden="true"
                v-if="loading"
              ></span
              >Finalizar adhesión</b-button
            >
            <b-button
              v-if="showElement([1])"
              class="mr-2"
              variant="success"
              @click="changeStatusAccession('aceptada')"
              :disabled="loading || uploadingFile"
              ><span
                class="spinner-border spinner-border-sm mr-2"
                role="status"
                aria-hidden="true"
                v-if="loading"
              ></span
              >Aceptar</b-button
            >
            <b-button
              v-if="showElement([1])"
              class="mr-2"
              variant="danger"
              @click="changeStatusAccession('rechazada')"
              :disabled="loading || uploadingFile"
              ><span
                class="spinner-border spinner-border-sm mr-2"
                role="status"
                aria-hidden="true"
                v-if="loading"
              ></span
              >Rechazar</b-button
            >
            <b-button @click="cancelForm()">Cancel</b-button>
          </div>
        </div>
      </template>
    </b-modal>
  </div>
</template>
